import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Icon, Text, Input } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import api from '../../utils/axios';
import type { RequirementGroupsProps, RequirementGroupsRef } from './RequirementGroups.types';
import type { RequirementGroup, Artifact } from './RequirementGroups.types';
import { GroupItem } from './components/GroupItem';
import { CreateGroupModal } from './components/CreateGroupModal';
import { EditGroupModal } from './components/EditGroupModal';
import { RenameArtifactPanel } from './components/RenameArtifactPanel';
import { IfHasPermission } from '@ui-library';
import { Permissions } from '../../constants';

// Skeleton Loading Component for Requirement Folders
const GroupItemSkeleton: React.FC<{ index: number }> = ({ index }) => {
  return (
    <div 
      className="animate-fade-in-up opacity-0"
      style={{ 
        animationDelay: `${index * 0.08}s`,
        animationFillMode: 'forwards'
      }}
    >
      <div className="flex items-center gap-bg-v2-md px-bg-v2-md py-bg-v2-sm rounded-bg-v2-sm bg-bg-primary-hover/50">
        {/* Chevron Skeleton */}
        <div className="w-4 h-4 skeleton-shimmer rounded flex-shrink-0"></div>
        
        {/* Folder Icon Skeleton */}
        <div className="w-4 h-4 skeleton-shimmer rounded flex-shrink-0"></div>
        
        {/* Folder Name Skeleton */}
        <div className="flex-1 min-w-0">
          <div className="h-5 skeleton-shimmer rounded w-3/4"></div>
        </div>
      </div>
    </div>
  );
};

export const RequirementGroups = forwardRef<RequirementGroupsRef, RequirementGroupsProps>(({
  project,
  onThreadSelect,
  onArtifactSelect,
  currentThreadId,
  handleCloseArtifactPreview,
  handleShareArtifact,
}, ref) => {
  const [groups, setGroups] = useState<RequirementGroup[]>([]);
  const { projectId, threadId: urlThreadId } = useParams<{ projectId: string, threadId: string }>();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingGroup, setEditingGroup] = useState<RequirementGroup | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [deletingArtifactId, setDeletingArtifactId] = useState<string | null>(null);
  const [artifactDownloadingIdentifier, setArtifactDownloadingIdentifier] = useState<string>('');
  const [renamingArtifact, setRenamingArtifact] = useState<Artifact | null>(null);
  const [renamingArtifactSaving, setRenamingArtifactSaving] = useState(false);
  const hasAutoSelectedRef = useRef(false);
  
  const isArtifactDownloading = (artifactId: string) => artifactDownloadingIdentifier === artifactId;

  useEffect(() => {
    loadGroups();
  }, [projectId]);

  // Auto-select first requirement folder when folders are loaded 
  useEffect(() => {
    if (urlThreadId) {
      // Check if the thread from URL still exists in groups (not deleted)
      const threadExists = groups.some(group => group.thread_id === urlThreadId);
      if (threadExists) {
        onThreadSelect(urlThreadId);
        hasAutoSelectedRef.current = true;
        return ;
      } 
    }
       
    if (!hasAutoSelectedRef.current && groups.length > 0) {
      const firstGroup = groups[0];
      if (firstGroup.thread_id) {
        onThreadSelect(firstGroup.thread_id);
        hasAutoSelectedRef.current = true;
      }
    } else if (groups.length === 0 && !loading) {
      onThreadSelect('');
    }
    
  }, [groups, loading, urlThreadId]);

  // Reset auto-select flag when component unmounts
  useEffect(() => {
    return () => {
      hasAutoSelectedRef.current = false;
    };
  }, []);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    reloadCurrentGroupArtifacts: () => {
      console.log('🔄 Reloading all requirement folders (includes artifacts)');
      loadGroups();
    },
    reloadGroups: () => {
      console.log('🔄 Reloading all requirement folders');
      loadGroups();
    }
  }), [groups, currentThreadId]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      // This now returns groups WITH artifact metadata (no content)
      const response = await api.get(`/requirement-groups/projects/${projectId}/groups`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Groups now include artifacts array with metadata
      const groupsData = response.data.groups || [];
      
      // Normalize artifact data structure
      const normalizedGroups = groupsData.map((group: any) => ({
        ...group,
        artifacts: (group.artifacts || []).map((artifact: any) => ({
          identifier: artifact.id,
          id: artifact.id,
          _id: artifact.id,
          type: artifact.type || 'text/plain',
          title: artifact.title || 'Untitled Artifact',
          content: '', // Content will be fetched on demand
          version: artifact.version
        }))
      }));
      
      setGroups(normalizedGroups);
    } catch (error) {
      console.error('Failed to load requirement folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;

    try {
      setCreating(true);
      const response = await api.post(
        `/requirement-groups/projects/${projectId}/groups`,
        {
          name: newGroupName,
          description: newGroupDescription || undefined
        },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setGroups([response.data, ...groups]);
      setShowCreateModal(false);
      setNewGroupName('');
      setNewGroupDescription('');
      
      if (response.data.thread_id) {
        onThreadSelect(response.data.thread_id);
      }
    } catch (error) {
      console.error('Failed to create requirement folder:', error);
      alert('Failed to create requirement folder. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !editName.trim()) return;

    try {
      const response = await api.put(
        `/requirement-groups/projects/${projectId}/groups/${editingGroup.id}`,
        {
          name: editName,
          description: editDescription || undefined
        },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setGroups(groups.map(g => g.id === editingGroup.id ? response.data : g));
      setEditingGroup(null);
      setEditName('');
      setEditDescription('');
    } catch (error) {
      console.error('Failed to update requirement folder:', error);
      alert('Failed to update requirement folder. Please try again.');
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm('Are you sure you want to delete this requirement folder? This will also delete the associated messages & artifacts.')) {
      return;
    }

    try {
      await api.delete(`/requirement-groups/projects/${projectId}/groups/${groupId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setGroups(groups.filter(g => g.id !== groupId));
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    } catch (error) {
      console.error('Failed to delete requirement folder:', error);
      alert('Failed to delete requirement folder. Please try again.');
    }
  };

  const startEdit = (group: RequirementGroup) => {
    setEditingGroup(group);
    setEditName(group.name);
    setEditDescription(group.description || '');
  };

  const cancelEdit = () => {
    setEditingGroup(null);
    setEditName('');
    setEditDescription('');
  };

  const handleArtifactOpenInNewTab = async (artifact: Artifact) => {
    try {
      const artifactId = artifact.id || artifact._id;
      const url = `/project/${projectId}/artifact/${artifactId}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to open artifact:', error);
      alert('Failed to open artifact. Please try again.');
    }
  };

  const downloadArtifact = async (identifier: string) => {
    try {
      setArtifactDownloadingIdentifier(identifier);
      
      const response = await api.post(`/projects/${projectId}/artifacts/download-with-temp`, {
        artifactId: identifier,
        messageId: `artifact_${identifier}`,
        title: 'Artifact',
        content: '',
        type: 'text/plain'
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.headers['content-disposition'].split(';')[1].split('=')[1];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setArtifactDownloadingIdentifier('');
    } finally {
      setArtifactDownloadingIdentifier('');
    }
  };

  const handleArtifactRename = (artifact: Artifact) => {
    setRenamingArtifact(artifact);
  };

  const handleArtifactRenameSave = async (artifactId: string, newTitle: string) => {
    try {
      setRenamingArtifactSaving(true);
      
      await api.put(
        `/projects/${projectId}/artifacts/${artifactId}`,
        { title: newTitle },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );

      // Reload all groups to refresh artifact list
      await loadGroups();
      
      setRenamingArtifact(null);
    } catch (error) {
      console.error('Failed to rename artifact:', error);
      alert('Failed to rename artifact. Please try again.');
      throw error;
    } finally {
      setRenamingArtifactSaving(false);
    }
  };

  const handleArtifactDelete = async (groupId: string, artifact: Artifact) => {
    const displayName = artifact.version 
      ? `${artifact.title} - V${artifact.version}` 
      : artifact.title;
    
    if (!confirm(`Are you sure you want to delete "${displayName}"?`)) {
      return;
    }

    try {
      setDeletingArtifactId(artifact.id);
      const artifactId = artifact.id || artifact._id;
      
      await api.delete(`/projects/${projectId}/artifacts/${artifactId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      await api.delete(`/requirement-groups/projects/${projectId}/groups/${groupId}/artifacts/${artifactId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      // Reload all groups to refresh artifact list
      loadGroups();
    } catch (error) {
      console.error('Failed to delete artifact:', error);
      alert('Failed to delete artifact. Please try again.');
    } finally {
      setDeletingArtifactId(null);
    }
  };

  // Filter groups based on search query
  const filteredGroups = groups.filter(group => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return group.name.toLowerCase().includes(query) || 
           group.description?.toLowerCase().includes(query);
  });

  return (
    <div className="flex flex-col h-full w-[280px] bg-bg-primary rounded-bg-v2-xl container-shadow-md border border-border-secondary flex-shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between min-h-[60px] px-bg-v2-xl py-bg-v2-xl border-b border-border-primary">
        <Text variant="text-sm" weight="semibold" color="primary">
          Requirement Folders
        </Text>
        <IfHasPermission
          permissions={project?.permissions || []}
          requires={Permissions.UpdateProjectPermission}
        >
        <Button
          label={<Icon name={IconName.Plus} size="sm" className="text-fg-secondary" inheritColor />}
            variant='ghost'
              size='small'
              title="Create Requirement Folder"
              onClick={() => setShowCreateModal(true)}
              className="!rounded-full !p-bg-v2-sm"
            />
          </IfHasPermission>
      </div>

      {/* Search Input */}
      <div style={{ padding: '24px 16px 12px 16px' }}>
        <Input
          id="search-groups"
          name="search-groups"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startIcon={<Icon name={IconName.Search} size="sm" className="text-text-tertiary" />}
          className="w-full"
        />
      </div>

      {/* Groups List */}
      <div className="flex-1 overflow-y-auto px-bg-v2-md flex flex-col gap-[2px] modern-scrollbar">
        {loading ? (
          // Show skeleton loading
          <div className="flex flex-col gap-bg-v2-sm">
            {[...Array(5)].map((_, index) => (
              <GroupItemSkeleton key={index} index={index} />
            ))}
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-bg-v2-8xl px-bg-v2-lg text-text-tertiary">
            {searchQuery.trim() ? (
              <>
                <Text variant="text-sm" color="tertiary" align="center">No folders found</Text>
                <Text variant="text-xs" color="tertiary" align="center" className="mt-2">
                  Try adjusting your search to find what you're looking for.
                </Text>
              </>
            ) : (
              <>
                <Text variant="text-sm" color="tertiary" align="center">No requirement folders yet.</Text>
                <Text variant="text-xs" color="tertiary" align="center" className="mt-2">
                  Create a folder to organize your requirements and artifacts.
                </Text>
              </>
            )}
          </div>
        ) : (
          filteredGroups.map((group, index) => {
            const isExpanded = expandedGroups.has(group.id);
            const artifacts = group.artifacts || []; // Use artifacts from group directly
            const isActiveThread = group.thread_id === currentThreadId;

            return (
              <div
                key={group.id}
                className="animate-fade-in-up-smooth opacity-0"
                style={{
                  animationDelay: `${index * 0.05}s`,
                  animationFillMode: 'forwards'
                }}
              >
                <GroupItem
                  group={group}
                  isExpanded={isExpanded}
                  isActiveThread={isActiveThread}
                  artifacts={artifacts}
                  permissions={project?.permissions || []}
                  onToggle={() => toggleGroup(group.id)}
                  onSelect={() => {
                    if (group.thread_id) {
                      onThreadSelect(group.thread_id);
                    }
                  }}
                  onEdit={() => startEdit(group)}
                  onDelete={() => handleDeleteGroup(group.id)}
                  onArtifactSelect={(artifact) => {
                    // Only switch thread if it's different from the current thread
                    if (group.thread_id && group.thread_id !== currentThreadId) {
                      onThreadSelect(group.thread_id);
                    }
                    // Always open the artifact preview
                    onArtifactSelect(artifact);
                  }}
                  onArtifactOpenInNewTab={handleArtifactOpenInNewTab}
                  onArtifactDownload={downloadArtifact}
                  onArtifactRename={handleArtifactRename}
                  onArtifactDelete={(artifact) => handleArtifactDelete(group.id, artifact)}
                  deletingArtifactId={deletingArtifactId}
                  isArtifactDownloading={isArtifactDownloading}
                  handleCloseArtifactPreview={handleCloseArtifactPreview}
                  handleShareArtifact={handleShareArtifact}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Create Modal */}
      <CreateGroupModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewGroupName('');
          setNewGroupDescription('');
        }}
        onSubmit={handleCreateGroup}
        groupName={newGroupName}
        setGroupName={setNewGroupName}
        groupDescription={newGroupDescription}
        setGroupDescription={setNewGroupDescription}
        creating={creating}
      />

      {/* Edit Modal */}
      <EditGroupModal
        isOpen={!!editingGroup}
        onClose={cancelEdit}
        onSubmit={handleUpdateGroup}
        groupName={editName}
        setGroupName={setEditName}
        groupDescription={editDescription}
        setGroupDescription={setEditDescription}
      />

      {/* Rename Artifact Panel */}
      <RenameArtifactPanel
        isOpen={!!renamingArtifact}
        artifact={renamingArtifact}
        onClose={() => setRenamingArtifact(null)}
        onSave={handleArtifactRenameSave}
        isSaving={renamingArtifactSaving}
      />
    </div>
  );
});

RequirementGroups.displayName = 'RequirementGroups';

export default RequirementGroups;

